<template>
  <div class="min-h-screen bg-[#F8FAFB] p-4">
    <div class="max-w-md mx-auto pt-8">
      <!-- Logo Section -->
      <div class="logo-title-container">
        <div class="logo-title-row">
          <!-- <img src="@/assets/AITRA Logo.png" alt="AITRA Logo" class="aitra-logo"/> -->
            <span class="aitra-title">AITRA</span>
          </div>
        <div class="aitra-subtitle mb-12">Beyond Words, Across Worlds</div>
      </div>

      <!-- Input Form -->
      <div class="mb-6 border border-[#D1E0ED] shadow-lg rounded-2xl bg-white">
        <div class="p-6 space-y-4">
          <div class="space-y-2">
            <label for="name" class="text-[#0033A0] font-semibold">Name</label>
            <input
              id="name"
              v-model="name"
              placeholder="Enter your name"
              class="w-full px-4 py-3 bg-[#E8EFF5] text-[#1A1A1A] rounded-2xl border border-[#D1E0ED] focus:ring-2 focus:ring-[#0033A0] focus:outline-none placeholder-gray-400"
            />
          </div>
          <div class="space-y-2">
            <label class="text-[#0033A0] font-semibold">Language</label>
            <div class="custom-select" @click="toggleDropdown">
              <div class="selected">
                {{ selectedLabel || 'Select language' }}
              </div>
              <ul v-if="dropdownOpen" class="dropdown-list">
                <li
                  v-for="lang in languages"
                  :key="lang.value"
                  @click.stop="selectLanguage(lang.value)"
                  :class="{ selected: lang.value === language }"
                >
                  {{ languageToEnglishName[lang.value] }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="space-y-4">
        <button
          @click="generateMeetingId"
          class="w-full h-12 bg-[#0033A0] hover:bg-[#002580] text-white text-base font-bold shadow-sm rounded-2xl transition-colors duration-150 flex items-center justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 mr-2 inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          Create Meeting
        </button>

        <button
          @click="showJoinModal = true"
          class="w-full h-12 border-2 border-[#0033A0] bg-transparent text-[#0033A0] text-base font-bold rounded-2xl shadow-sm hover:bg-[rgba(0,51,160,0.05)] transition-colors duration-150"
        >
          Join Meeting
        </button>
      </div>
    </div>

    <!-- Create Meeting Modal -->
    <Transition name="modal">
      <CreateMeetingModal
        v-if="showCreateModal"
        :meeting-id="generatedMeetingId"
        :is-copied="isCopied"
        @close="showCreateModal = false"
        @copy="copyMeetingId"
        @join="handleCreateAndJoin"
      />
    </Transition>

    <!-- Join Meeting Modal -->
    <Transition name="modal">
      <JoinMeetingModal
        v-if="showJoinModal"
        v-model:meeting-id="meetingId"
        @close="showJoinModal = false"
        @join="handleJoinExisting"
      />
    </Transition>
  </div>
</template>
  
<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import CreateMeetingModal from '../components/modals/MobileCreateMeetingModal.vue'
import JoinMeetingModal from '../components/modals/MobileJoinMeetingModal.vue'
import '@/assets/styles/mobile-modal.css'
import { apiClientV1, restApiClient } from '@/api/apiConfig.js';
import { languageOptions, languageToEnglishName } from '@/constants/languages.js'

const router = useRouter()

const languages = languageOptions

const name = ref('')
const language = ref('')
const meetingId = ref('')
const showCreateModal = ref(false)
const showJoinModal = ref(false)
const generatedMeetingId = ref('')
const isCopied = ref(false)
const dropdownOpen = ref(false)

const selectedLabel = computed(() =>
  language.value ? languageToEnglishName[language.value] : ''
)

// 안정적인 UUID 생성 헬퍼 함수
const createUUID = () => {
  return (window.crypto && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
}

onMounted(() => {
  // 로컬 스토리지에 'mobile_user' 키가 있는지 확인
  if (!localStorage.getItem('mobile_user')) {
    const uuid = createUUID();
    const primaryKey = `mobile_${uuid}`;
    localStorage.setItem('mobile_user', primaryKey);
  }
});

const generateMeetingId = () => {
  const uuid = createUUID();
  const shortId = uuid.substring(0, 8).toUpperCase()
  generatedMeetingId.value = shortId
  showCreateModal.value = true
  isCopied.value = false
}

const copyMeetingId = async () => {
  try {
    await navigator.clipboard.writeText(generatedMeetingId.value)
    isCopied.value = true
    setTimeout(() => isCopied.value = false, 2000)
  } catch (err) {
    alert('Failed to copy Meeting ID.')
  }
}

const prepareAndNavigate = (targetMeetingId) => {
  const mobileUserId = localStorage.getItem('mobile_user');
  const userData = {
    id: mobileUserId,
    name: name.value.trim(),
    language: languageToEnglishName[language.value] || language.value,
    meetingId: targetMeetingId,
  };

  localStorage.setItem("meetsync-user", JSON.stringify(userData));
  router.push(`/mobile/meeting/${targetMeetingId}`);
}

// 회의 생성 버튼 클릭 시
const handleCreateAndJoin = () => {
  if (!name.value.trim() || !language.value) {
    alert('Please enter your name and select a language.');
    showCreateModal.value = false;
    return;
  }
  prepareAndNavigate(generatedMeetingId.value);
}

// 회의 참여 버튼 클릭 시
const handleJoinExisting = async () => {
  const enteredId = meetingId.value.trim().toUpperCase();

  if (!enteredId) {
    alert('Please enter a Meeting ID to join.');
    return;
  }
  if (!name.value.trim() || !language.value) {
    alert('Please enter your name and select a language.');
    showJoinModal.value = false;
    return;
  }

  //Spring API를 통해 회의 검증
  try {
    const response = await restApiClient.get(`/restapi/meetings/id?code=${enteredId}`);
    prepareAndNavigate(response.meetingId);
    return;
  } catch(error) {
    console.error('Error fetching meeting details:', error);
  }

  //Model 서버를 통해 회의 검증
  try {
    await apiClientV1.get(`/api/v1/mobile/meetings/verify/${enteredId}`);
    prepareAndNavigate(enteredId);
  } catch (error) {
    if (error.response && error.response.status === 404) {
      alert('The Meeting ID is not valid.');
    } else {
      alert('An error occurred while verifying the Meeting ID.');
    }
    showJoinModal.value = false;
  }
}

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value
}
function selectLanguage(val) {
  language.value = val
  dropdownOpen.value = false
}
</script>
<style>
.logo-title-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 6px;
}

.logo-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: center; /* 중앙 정렬, 필요시 space-between으로 변경 */
  gap: 10px;               /* 로고와 텍스트 사이 간격 */
  margin-bottom: 2px;
}

.aitra-logo {
  width: 52px;   /* 이미지 크기 조정 (예시) */
  height: 52px;
  object-fit: contain;
  /* flex-shrink: 0; */
  /* margin-top: 4px; */
}

/* .aitra-title-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
} */

.aitra-title {
  font-family: 'Zen Tokyo Zoo', sans-serif;
  font-size: 2.3rem;
  font-weight: bold;
  color: #0033A0;
  letter-spacing: 0.1em;
  /* margin-left: 25px; 타이틀을 오른쪽으로 이동 */
  /* margin-bottom: 2px;
  line-height: 1.1; */
}

.aitra-subtitle {
  color: #0033A0;
  font-size: 1.08rem;
  font-weight: 500;
  opacity: 0.8;
}

.custom-select {
  position: relative;
  width: 100%;
  cursor: pointer;
}
.selected {
  background: #E8EFF5;
  color: #1A1A1A;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid #D1E0ED;
}
.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #D1E0ED;
  border-radius: 16px;
  max-height: 220px; /* 약 5개 항목 높이 */
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.dropdown-list li {
  padding: 12px;
  color: #1A1A1A;
}
.dropdown-list li.selected,
.dropdown-list li:hover {
  background: #0033A0;
  color: #fff;
}
</style>